import { reachabilityMonitor } from '@alucio/core'
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito'
import { AuthConfig, AuthTokens, TokenProvider } from '@aws-amplify/core'
import logger from 'src/utils/logger'
import { Subscription } from 'rxjs';

/**
 * Custom Token Provider that always returns cached tokens if offline
 */
class BeaconCognitoTokenProvider implements TokenProvider {
  static LOCK_NAME: string = 'BEACON_TOKEN_PROVIDER_LOCK'

  isOnline: boolean
  reachabilitySubscription: Subscription

  constructor() {
    this.isOnline = navigator.onLine
    this.reachabilitySubscription = reachabilityMonitor.subscribe(v => {
      if (this.isOnline !== v.online) {
        logger.auth.signIn.authenticator.debug('Reachability Event got !!',
          { isOnline: this.isOnline, newStatus: v.online })
        this.isOnline = v.online
      }
    })
  }

  /**
   * Retrieves authentication tokens with iOS PWA lock timeout protection.
   *
   * This method addresses BEAC-7353 where iPad PWA users couldn't log in after logout
   * due to GraphQL calls not executing. The issue was caused by navigator.locks
   * getting stuck in iOS, preventing subsequent authentication requests.
   *
   * The solution implements a 3-second timeout using AbortController to prevent
   * indefinite lock waiting, with retry logic that steals the lock if needed.
   *
   * @param forceRefresh - Whether to force refresh the tokens
   * @param retryCount - Current retry attempt (used internally for lock stealing)
   * @returns Promise resolving to authentication tokens or null
   * @throws Error if lock acquisition fails after max retries
   */
  async getTokens({ forceRefresh }: { forceRefresh?: boolean; }, retryCount: number = 0): Promise<AuthTokens | null> {
    try {
      logger.auth.signIn.authenticator.debug(`Token fetch!! ${this.isOnline}`)
      if (this.isOnline) {
        // iOS PWA Lock Timeout Protection (BEAC-7353)
        // Prevents indefinite lock waiting that blocks GraphQL calls after logout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3 * 1000); // 3 second timeout

        try {
          // Use lock to prevent concurrent token requests and leverage caching
          // The timeout ensures iOS PWA doesn't get stuck waiting for lock release
          const result = await navigator.locks.request(
            BeaconCognitoTokenProvider.LOCK_NAME,
            { mode: 'exclusive', signal: controller.signal },
            async () => {
              return await cognitoUserPoolsTokenProvider.getTokens({ forceRefresh })
            },
          );

          // Clear timeout since operation completed successfully
          clearTimeout(timeoutId);
          return result;
        } catch (error) {
          // Clear timeout in all error cases
          clearTimeout(timeoutId);

          if (error instanceof Error && error.name === 'AbortError') {
            // Lock timeout occurred - attempt to steal the lock and retry
            const maxRetries = 3;
            if (retryCount >= maxRetries) {
              logger.auth.signIn.authenticator.error(`Failed to acquire token lock after ${maxRetries} attempts`);
              throw new Error(`Failed to acquire token lock after ${maxRetries} attempts`);
            }

            // Force release the stuck lock by stealing it
            logger.auth.signIn.authenticator.warn(`Stealing token lock (attempt ${retryCount + 1}/${maxRetries})`)
            await navigator.locks.request(
              BeaconCognitoTokenProvider.LOCK_NAME,
              { mode: 'exclusive', steal: true }, async () => {
                logger.auth.signIn.authenticator.warn('Token lock released')
              },
            )

            // Retry token acquisition with incremented retry count
            return this.getTokens({ forceRefresh }, retryCount + 1)
          }
          throw error
        }
      } else {
        // We bypass the amplify token provider's validity check when offline
        // returning expired tokens when offline is ok since we're offline and won't be
        // able to make requests
        const tokens = await cognitoUserPoolsTokenProvider.tokenOrchestrator.getTokenStore().loadTokens()
        return tokens
      }
    } catch (e) {
      logger.auth.warn('Error during token fetch', e)
      throw e;
    }
  }

  setAuthConfig(authConfig: AuthConfig) {
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig)
  }
}

const beaconCognitoTokenProvider = new BeaconCognitoTokenProvider()
export { beaconCognitoTokenProvider }
